import {
    builderFormToCalculatorInput,
    calculateProductPricing,
    calculateTransportCostRate,
    DEFAULT_PRICING_SETTINGS,
    entryRonExVatBeforeCostUplift,
    formatNumericInputValue,
    LIST_PRICE_EURO_DIVISOR,
    mergeFormWithFurnizorPricing,
    mergeListPricingSettings,
    parseNumericInput,
    persistActiveFurnizorPricingFromRoot,
    ratioToPercentageInput,
    roundMoney,
} from '@/utils/priceListProductCalculator';
import {
    normalizeCurrencyCode,
    normalizeTransportCostBreakdown,
    resolveTransportConverterRate,
    syncTransportCostFromBreakdown,
    TRANSPORT_COST_MODE_FORMULA,
    TRANSPORT_COST_MODE_PERCENT,
} from '@/utils/transportCostBreakdown';
import {
    getStampDutyAmountRon,
    normalizeStampDutyBreakdown,
    normalizeStampDutyCurrency,
    STAMP_DUTY_MODE_FORMULA,
    STAMP_DUTY_MODE_MANUAL,
    syncStampDutyFromBreakdown,
} from '@/utils/stampDutyBreakdown';

/**
 * Convert supplier list prices when the working currency changes, using the
 * exchange rates configured on the price list (not daily BNR/ECB rates).
 *
 * @param {Record<string, unknown>} form
 * @param {string} fromCurrency
 * @param {string} toCurrency
 * @param {Record<string, unknown>} [pricingSettings]
 * @param {{ convertNew?: boolean, convertOld?: boolean }} [options]
 * @returns {boolean} true when at least one price field was rewritten
 */
export function convertSupplierListPricesForCurrencyChange(
    form,
    fromCurrency,
    toCurrency,
    pricingSettings = DEFAULT_PRICING_SETTINGS,
    options = {},
) {
    const from = normalizeCurrencyCode(fromCurrency || 'ron');
    const to = normalizeCurrencyCode(toCurrency || 'ron');
    if (from === to) {
        return false;
    }

    const convertNew = options.convertNew !== false;
    const convertOld = options.convertOld !== false;
    if (!convertNew && !convertOld) {
        return false;
    }

    const settings = mergeListPricingSettings(pricingSettings);
    const rate = resolveTransportConverterRate(from, to, settings);
    if (!(rate > 0)) {
        return false;
    }

    let changed = false;
    const convertField = (fieldKey) => {
        const raw = form[fieldKey];
        if (raw === null || raw === undefined || raw === '') {
            return;
        }
        const value = parseNumericInput(raw);
        if (!(value > 0)) {
            return;
        }
        const next = formatNumericInputValue(roundMoney(value * rate, 4), 4);
        if (String(raw) !== String(next)) {
            form[fieldKey] = next;
            changed = true;
        }
    };

    if (convertNew) {
        convertField('supplierListPriceNew');
    }
    if (convertOld) {
        convertField('supplierListPriceOld');
    }
    return changed;
}

/**
 * Convert currency-denominated fields on a pricing baseline snapshot so
 * %-change badges do not treat a unit conversion as a price jump
 * (e.g. 100 EUR → 535 RON must not show +435%).
 *
 * Only amounts expressed in the supplier working currency are converted.
 * RON result fields (entry/list) stay as-is.
 *
 * @param {Record<string, unknown>|null|undefined} baseline
 * @param {string} fromCurrency
 * @param {string} toCurrency
 * @param {Record<string, unknown>} [pricingSettings]
 * @returns {Record<string, unknown>|null|undefined}
 */
export function convertPricingBaselineForCurrencyChange(
    baseline,
    fromCurrency,
    toCurrency,
    pricingSettings = DEFAULT_PRICING_SETTINGS,
) {
    if (!baseline || typeof baseline !== 'object') {
        return baseline;
    }
    const from = normalizeCurrencyCode(fromCurrency || 'ron');
    const to = normalizeCurrencyCode(toCurrency || 'ron');
    if (from === to) {
        return baseline;
    }

    const settings = mergeListPricingSettings(pricingSettings);
    const rate = resolveTransportConverterRate(from, to, settings);
    if (!(rate > 0)) {
        return baseline;
    }

    const convertAmount = (raw, decimals = 4) => {
        if (raw === null || raw === undefined || raw === '') {
            return raw;
        }
        const value = parseNumericInput(raw);
        if (!(value > 0)) {
            return raw;
        }
        return formatNumericInputValue(roundMoney(value * rate, decimals), decimals);
    };

    return {
        ...baseline,
        currency: to,
        // Supplier working-currency amounts (not RON results).
        supplierListPriceNew: convertAmount(baseline.supplierListPriceNew, 4),
        supplierListPriceOld: Object.prototype.hasOwnProperty.call(
            baseline,
            'supplierListPriceOld',
        )
            ? convertAmount(baseline.supplierListPriceOld, 4)
            : baseline.supplierListPriceOld,
        finalEntryPriceExVat: convertAmount(baseline.finalEntryPriceExVat, 4),
        netEntryPriceExVat: Object.prototype.hasOwnProperty.call(
            baseline,
            'netEntryPriceExVat',
        )
            ? convertAmount(baseline.netEntryPriceExVat, 4)
            : baseline.netEntryPriceExVat,
    };
}

/**
 * Convert a single supplier-list amount with list exchange rates.
 *
 * @param {unknown} raw
 * @param {string} fromCurrency
 * @param {string} toCurrency
 * @param {Record<string, unknown>} [pricingSettings]
 * @returns {unknown}
 */
export function convertSupplierListPriceAmount(
    raw,
    fromCurrency,
    toCurrency,
    pricingSettings = DEFAULT_PRICING_SETTINGS,
) {
    if (raw === null || raw === undefined || raw === '') {
        return raw;
    }
    const from = normalizeCurrencyCode(fromCurrency || 'ron');
    const to = normalizeCurrencyCode(toCurrency || 'ron');
    if (from === to) {
        return raw;
    }
    const value = parseNumericInput(raw);
    if (!(value > 0)) {
        return raw;
    }
    const rate = resolveTransportConverterRate(
        from,
        to,
        mergeListPricingSettings(pricingSettings),
    );
    if (!(rate > 0)) {
        return raw;
    }
    return formatNumericInputValue(roundMoney(value * rate, 4), 4);
}

/** Computed result keys shown in the builder "Rezultate calculate" section. */
export const PRICING_RESULT_FIELD_KEYS = [
    'finalEntryPriceExVat',
    'entryPriceRonExVat',
    'entryPriceRonIncVat',
    'listPriceRonExVat',
    'listPriceRonIncVatFinal',
    'listPriceEuroExVat',
];

/** Result keys derived only from forward calculation — not user-editable. */
export const PRICING_READONLY_RESULT_FIELD_KEYS = [
    'finalEntryPriceExVat',
    'entryPriceRonExVat',
    'entryPriceRonIncVat',
    'listPriceEuroExVat',
];

/** Input keys whose edits drive forward pricing calculations. */
export const PRICING_INPUT_FIELD_KEYS = [
    'supplierListPriceNew',
    'currency',
    'discounts',
    'discount1',
    'discount2',
    'cashDiscount',
    'marketingDiscount',
    'transportCostRate',
    'transportCostBreakdown',
    'stampDutyAmount',
    'stampDutyCurrency',
    'stampDutyBreakdown',
    'squareMetersPerTruck',
    'markupReference',
    'markupActual',
];

/**
 * @param {Record<string, unknown>} form
 * @returns {number}
 */
export function netEntryMultiplierFromForm(form) {
    const input = builderFormToCalculatorInput(form);
    let multiplier = 1;

    const discountRatios = Array.isArray(input.discounts) && input.discounts.length
        ? input.discounts.map((value) => parseNumericInput(value)).filter((ratio) => ratio > 0)
        : [
            parseNumericInput(input.discount1),
            parseNumericInput(input.discount2),
            parseNumericInput(input.cashDiscount),
        ].filter((ratio) => ratio > 0);

    discountRatios.forEach((ratio) => {
        multiplier *= (1 - ratio);
    });

    const marketingRatio = parseNumericInput(input.marketingDiscount);
    if (marketingRatio > 0) {
        multiplier *= (1 + marketingRatio);
    }

    return multiplier > 0 ? multiplier : 1;
}

/**
 * @param {Record<string, unknown>} form
 * @param {unknown} netEntryExVat
 * @returns {number}
 */
export function supplierListPriceFromNetEntry(form, netEntryExVat) {
    const netEntry = parseNumericInput(netEntryExVat);
    if (!(netEntry > 0)) {
        return 0;
    }
    return roundMoney(netEntry / netEntryMultiplierFromForm(form), 4);
}

/**
 * @param {unknown} netEntryExVat
 * @param {unknown} transportCostRate
 * @returns {number}
 */
export function finalEntryFromNetEntry(netEntryExVat, transportCostRate) {
    const netEntry = parseNumericInput(netEntryExVat);
    const transportRate = parseNumericInput(transportCostRate);
    if (!(netEntry > 0)) {
        return 0;
    }
    return roundMoney(netEntry * (1 + transportRate), 4);
}

/**
 * @param {unknown} finalEntryExVat
 * @param {unknown} transportCostRate
 * @returns {number}
 */
export function netEntryFromFinalEntry(finalEntryExVat, transportCostRate) {
    const finalEntry = parseNumericInput(finalEntryExVat);
    const transportRate = parseNumericInput(transportCostRate);
    const divisor = 1 + transportRate;
    if (!(finalEntry > 0) || divisor <= 0) {
        return 0;
    }
    return roundMoney(finalEntry / divisor, 4);
}

/**
 * @param {unknown} finalEntryExVat
 * @param {string} currency
 * @param {Record<string, unknown>} pricingSettings
 * @returns {number}
 */
export function entryPriceRonExVatFromFinalEntry(finalEntryExVat, currency, pricingSettings) {
    const computed = calculateProductPricing(
        {
            supplierListPriceNew: parseNumericInput(finalEntryExVat),
            currency,
            transportCostRate: 0,
            markupReference: 0,
        },
        pricingSettings,
    );
    const normalizedCurrency = String(currency || 'ron').trim().toLowerCase();
    if (normalizedCurrency === 'ron') {
        return roundMoney(parseNumericInput(finalEntryExVat), 2);
    }
    const appliedRate = parseNumericInput(computed.appliedExchangeRate);
    if (appliedRate <= 0) {
        return 0;
    }
    return roundMoney(parseNumericInput(finalEntryExVat) * appliedRate, 2);
}

/**
 * @param {unknown} entryPriceRonExVat
 * @param {string} currency
 * @param {Record<string, unknown>} pricingSettings
 * @returns {number}
 */
export function finalEntryFromEntryPriceRonExVat(entryPriceRonExVat, currency, pricingSettings) {
    const entryRon = parseNumericInput(entryPriceRonExVat);
    if (!(entryRon > 0)) {
        return 0;
    }
    const normalizedCurrency = String(currency || 'ron').trim().toLowerCase();
    if (normalizedCurrency === 'ron') {
        return roundMoney(entryRon, 4);
    }
    const settings = mergeListPricingSettings(pricingSettings);
    const probe = calculateProductPricing(
        {
            supplierListPriceNew: 1,
            currency,
            transportCostRate: 0,
            markupReference: 0,
        },
        settings,
    );
    const appliedRate = parseNumericInput(probe.appliedExchangeRate);
    if (appliedRate <= 0) {
        return 0;
    }
    return roundMoney(entryRon / appliedRate, 4);
}

/**
 * @param {{
 *   transportCostRate?: unknown,
 *   supplierListPriceNew?: unknown,
 *   squareMetersPerTruck?: unknown,
 * }} params
 * @returns {number}
 */
export function calculateTransportCostEstimated({
    transportCostRate,
    supplierListPriceNew,
    squareMetersPerTruck,
}) {
    const rate = parseNumericInput(transportCostRate);
    const supplierPrice = parseNumericInput(supplierListPriceNew);
    const squareMeters = parseNumericInput(squareMetersPerTruck);
    if (rate <= 0 || supplierPrice <= 0) {
        return 0;
    }
    if (squareMeters > 0) {
        return roundMoney(rate * supplierPrice * squareMeters, 2);
    }
    return roundMoney(rate * supplierPrice, 2);
}

/**
 * @param {Record<string, unknown>} form
 */
export function clearListPriceOverride(form) {
    form.overrideListPriceRonIncVatFinal = false;
    form.overrideListPriceRonIncVatFinalValue = '';
    form.useManualFinalPrice = false;
    form.manualListPriceRonIncVatFinal = '';
    form.priceAdjustmentRatio = '';
}

/**
 * Locks the current list price so upstream edits cannot recalculate it.
 * Prefers an existing override; otherwise uses a pre-edit snapshot.
 *
 * @param {Record<string, unknown>} form
 * @param {unknown} [preservedListPriceRonIncVatFinal]
 * @returns {boolean}
 */
export function preserveListPriceOverride(form, preservedListPriceRonIncVatFinal) {
    const existingOverride = parseNumericInput(form.overrideListPriceRonIncVatFinalValue);
    if (form.overrideListPriceRonIncVatFinal && existingOverride > 0) {
        return true;
    }
    const listPrice = parseNumericInput(preservedListPriceRonIncVatFinal);
    if (!(listPrice > 0)) {
        return false;
    }
    form.overrideListPriceRonIncVatFinal = true;
    form.overrideListPriceRonIncVatFinalValue = formatNumericInputValue(listPrice, 2);
    return true;
}

/**
 * Derives adaos actual from sticky list price vs current entry price.
 *
 * @param {Record<string, unknown>} form
 * @param {Record<string, unknown>} [pricingSettings]
 */
export function syncMarkupActualFromListOverride(form, pricingSettings = DEFAULT_PRICING_SETTINGS) {
    if (!form.overrideListPriceRonIncVatFinal) {
        return;
    }
    if (!(parseNumericInput(form.overrideListPriceRonIncVatFinalValue) > 0)) {
        return;
    }
    const computed = calculateFromForm(form, pricingSettings);
    form.markupActual = ratioToPercentageInput(computed.computedMarkupActual);
}

/**
 * @param {Record<string, unknown>} form
 * @param {Record<string, unknown>} [pricingSettings]
 */
export function syncTransportCostRateFromEstimated(form, pricingSettings = DEFAULT_PRICING_SETTINGS) {
    void pricingSettings;
    const estimated = String(form.transportCostEstimated ?? '').trim();
    if (!estimated) {
        return;
    }
    const rate = calculateTransportCostRate({
        transportCostEstimated: estimated,
        supplierListPriceNew: form.supplierListPriceNew,
        squareMetersPerTruck: form.squareMetersPerTruck,
    });
    if (rate <= 0) {
        form.transportCostRate = '';
        return;
    }
    const currentRatePercent = parseNumericInput(form.transportCostRate);
    const nextRatePercent = roundMoney(rate * 100, 2);
    if (
        currentRatePercent > 0 &&
        Math.abs(nextRatePercent - currentRatePercent) < 0.005
    ) {
        return;
    }
    form.transportCostRate = ratioToPercentageInput(rate, 2);
}

/**
 * @param {Record<string, unknown>} form
 * @param {Record<string, unknown>} pricingSettings
 * @returns {Record<string, unknown>}
 */
function calculateFromForm(form, pricingSettings) {
    return calculateProductPricing(
        builderFormToCalculatorInput(form),
        mergeListPricingSettings(pricingSettings),
    );
}

/**
 * @param {Record<string, unknown>} form
 * @param {unknown} value
 * @param {Record<string, unknown>} pricingSettings
 */
function applyNetEntryPriceEdit(form, value, pricingSettings) {
    void pricingSettings;
    const supplierNew = supplierListPriceFromNetEntry(form, value);
    form.supplierListPriceNew = supplierNew > 0
        ? formatNumericInputValue(supplierNew, 4)
        : '';
}

/**
 * @param {Record<string, unknown>} form
 * @param {unknown} value
 * @param {Record<string, unknown>} pricingSettings
 */
function applyFinalEntryPriceEdit(form, value, pricingSettings) {
    void pricingSettings;
    const input = builderFormToCalculatorInput(form);
    const netEntry = netEntryFromFinalEntry(value, input.transportCostRate);
    applyNetEntryPriceEdit(form, netEntry, pricingSettings);
}

/**
 * @param {Record<string, unknown>} form
 * @param {unknown} value
 * @param {Record<string, unknown>} pricingSettings
 */
function applyEntryPriceRonExVatEdit(form, value, pricingSettings) {
    const entryRonWithUplifts = parseNumericInput(value);
    const calculatorInput = builderFormToCalculatorInput(form);
    const entryRonBeforeUplift = entryRonExVatBeforeCostUplift(
        entryRonWithUplifts,
        calculatorInput,
    );
    const stampDutyAmountRon = getStampDutyAmountRon(
        form.stampDutyAmount,
        form.stampDutyCurrency,
        pricingSettings,
    );
    const entryRonRaw = roundMoney(entryRonBeforeUplift - stampDutyAmountRon, 2);
    const finalEntry = finalEntryFromEntryPriceRonExVat(
        entryRonRaw > 0 ? entryRonRaw : entryRonBeforeUplift,
        form.currency,
        pricingSettings,
    );
    applyFinalEntryPriceEdit(form, finalEntry, pricingSettings);
}

/**
 * @param {Record<string, unknown>} form
 * @param {unknown} value
 * @param {Record<string, unknown>} pricingSettings
 */
function applyEntryPriceRonIncVatEdit(form, value, pricingSettings) {
    const settings = mergeListPricingSettings(pricingSettings);
    const vatRate = parseNumericInput(settings.vatRate) || DEFAULT_PRICING_SETTINGS.vatRate;
    const entryRonExVat = vatRate > 0
        ? roundMoney(parseNumericInput(value) / vatRate, 2)
        : 0;
    applyEntryPriceRonExVatEdit(form, entryRonExVat, pricingSettings);
}

/**
 * @param {Record<string, unknown>} form
 * @param {unknown} value
 * @param {Record<string, unknown>} pricingSettings
 */
function applyListPriceRonExVatEdit(form, value, pricingSettings) {
    const settings = mergeListPricingSettings(pricingSettings);
    const vatRate = parseNumericInput(settings.vatRate) || DEFAULT_PRICING_SETTINGS.vatRate;
    const listIncVat = vatRate > 0
        ? roundMoney(parseNumericInput(value) * vatRate, 2)
        : 0;
    applyListPriceRonIncVatFinalEdit(form, listIncVat, pricingSettings);
}

/**
 * @param {Record<string, unknown>} form
 * @param {unknown} value
 * @param {Record<string, unknown>} pricingSettings
 */
function applyListPriceEuroExVatEdit(form, value, pricingSettings) {
    const listRonExVat = roundMoney(
        parseNumericInput(value) * LIST_PRICE_EURO_DIVISOR,
        2,
    );
    applyListPriceRonExVatEdit(form, listRonExVat, pricingSettings);
}

/**
 * @param {Record<string, unknown>} form
 * @param {unknown} value
 * @param {Record<string, unknown>} pricingSettings
 */
function applyListPriceRonIncVatFinalEdit(form, value, pricingSettings) {
    const parsed = parseNumericInput(value);
    if (!(parsed > 0)) {
        clearListPriceOverride(form);
        form.markupActual = '';
        return;
    }
    form.overrideListPriceRonIncVatFinal = true;
    form.overrideListPriceRonIncVatFinalValue = formatNumericInputValue(parsed, 2);
    const computed = calculateFromForm(form, pricingSettings);
    form.markupActual = ratioToPercentageInput(computed.computedMarkupActual);
}

/**
 * Adaos actual drives list price: pret listă = pret intrare × (1 + adaos).
 * Clears any sticky list override so the calculator applies the formula;
 * listă remains manually editable and then re-derives adaos.
 *
 * @param {Record<string, unknown>} form
 * @param {unknown} value
 * @param {Record<string, unknown>} pricingSettings
 */
function applyMarkupActualEdit(form, value, pricingSettings) {
    void pricingSettings;
    const raw = value ?? form.markupActual;
    const trimmed = String(raw ?? '').trim();
    if (trimmed === '') {
        form.markupActual = '';
        clearListPriceOverride(form);
        return;
    }
    form.markupActual = formatNumericInputValue(parseNumericInput(raw), 2);
    clearListPriceOverride(form);
}

/**
 * Upstream edits that change entry price must keep an existing list price sticky
 * and recompute adaos actual from list vs entry. If no list price exists yet
 * (new product / only supplier price filled), do not invent one.
 *
 * @param {Record<string, unknown>} form
 * @param {Record<string, unknown>} pricingSettings
 * @param {unknown} preservedListPriceRonIncVatFinal
 * @param {{ markupActualManual?: boolean }} result
 */
function keepListPriceStickyAndSyncMarkup(
    form,
    pricingSettings,
    preservedListPriceRonIncVatFinal,
    result,
) {
    let preserved = preservedListPriceRonIncVatFinal;
    if (!(parseNumericInput(preserved) > 0)) {
        const existingOverride = parseNumericInput(
            form.overrideListPriceRonIncVatFinalValue,
        );
        if (form.overrideListPriceRonIncVatFinal && existingOverride > 0) {
            preserved = existingOverride;
        }
    }
    if (!(parseNumericInput(preserved) > 0)) {
        // No selling price yet — leave list empty; do not sync −100% adaos.
        result.markupActualManual = false;
        return;
    }
    preserveListPriceOverride(form, preserved);
    syncMarkupActualFromListOverride(form, pricingSettings);
    result.markupActualManual = false;
}

/**
 * Applies a manual edit to a pricing field and updates dependent fields using
 * the existing forward/inverse calculation rules.
 *
 * @param {Record<string, unknown>} form
 * @param {string} fieldKey
 * @param {unknown} rawValue
 * @param {Record<string, unknown>} [pricingSettings]
 * @param {{
 *   preservedListPriceRonIncVatFinal?: unknown,
 *   convertSupplierListPriceNew?: boolean,
 *   convertSupplierListPriceOld?: boolean,
 * }} [options]
 * @returns {{ markupActualManual?: boolean }}
 */
export function applyPricingFieldEdit(
    form,
    fieldKey,
    rawValue,
    pricingSettings = DEFAULT_PRICING_SETTINGS,
    options = {},
) {
    const result = {};
    const preservedListPrice = options.preservedListPriceRonIncVatFinal;

    switch (fieldKey) {
        case 'markupReference':
            break;
        case 'markupActual':
            // Adaos actual drives pret listă RON (cu/fără TVA) via formula;
            // listă remains manually editable and then re-derives adaos.
            applyMarkupActualEdit(form, rawValue, pricingSettings);
            result.markupActualManual = true;
            break;
        case 'transportCostRate':
            // Keep formula breakdown; manual % overrides computed rate only.
            {
                const breakdown = normalizeTransportCostBreakdown(
                    form.transportCostBreakdown,
                );
                if (breakdown.mode === TRANSPORT_COST_MODE_FORMULA) {
                    breakdown.rateIsManual = true;
                    form.transportCostBreakdown = breakdown;
                }
            }
            keepListPriceStickyAndSyncMarkup(
                form,
                pricingSettings,
                preservedListPrice,
                result,
            );
            break;
        case 'transportCostBreakdown':
            if (rawValue && typeof rawValue === 'object') {
                const breakdown = normalizeTransportCostBreakdown(rawValue);
                // Formula apply recomputes %; percent/reset keeps an explicit rate.
                if (breakdown.mode === TRANSPORT_COST_MODE_FORMULA) {
                    breakdown.rateIsManual = false;
                }
                form.transportCostBreakdown = breakdown;
            }
            syncTransportCostFromBreakdown(form);
            if (
                form.transportCostBreakdown?.mode === TRANSPORT_COST_MODE_PERCENT
                && String(form.transportCostRate ?? '').trim() === ''
            ) {
                // Reset / clear formula must land on 0%, not empty (−100% hint).
                form.transportCostRate = '0';
            }
            keepListPriceStickyAndSyncMarkup(
                form,
                pricingSettings,
                preservedListPrice,
                result,
            );
            break;
        case 'stampDutyAmount':
            {
                const breakdown = normalizeStampDutyBreakdown(
                    form.stampDutyBreakdown,
                );
                if (breakdown.mode === STAMP_DUTY_MODE_FORMULA) {
                    breakdown.amountIsManual = true;
                    form.stampDutyBreakdown = breakdown;
                }
            }
            keepListPriceStickyAndSyncMarkup(
                form,
                pricingSettings,
                preservedListPrice,
                result,
            );
            break;
        case 'stampDutyCurrency':
            form.stampDutyCurrency = normalizeStampDutyCurrency(
                rawValue || form.stampDutyCurrency || 'ron',
            );
            {
                const breakdown = normalizeStampDutyBreakdown(
                    form.stampDutyBreakdown,
                );
                if (breakdown.mode === STAMP_DUTY_MODE_FORMULA) {
                    // Manual currency override while formula is open — keep amount,
                    // mark amount manual so formula does not overwrite currency.
                    breakdown.amountIsManual = true;
                    form.stampDutyBreakdown = breakdown;
                }
            }
            keepListPriceStickyAndSyncMarkup(
                form,
                pricingSettings,
                preservedListPrice,
                result,
            );
            break;
        case 'stampDutyBreakdown':
            if (rawValue && typeof rawValue === 'object') {
                const breakdown = normalizeStampDutyBreakdown(rawValue);
                if (breakdown.mode === STAMP_DUTY_MODE_FORMULA) {
                    breakdown.amountIsManual = false;
                }
                form.stampDutyBreakdown = breakdown;
            }
            syncStampDutyFromBreakdown(form, pricingSettings);
            if (
                form.stampDutyBreakdown?.mode === STAMP_DUTY_MODE_MANUAL
                && String(form.stampDutyAmount ?? '').trim() === ''
            ) {
                form.stampDutyAmount = '0';
            }
            keepListPriceStickyAndSyncMarkup(
                form,
                pricingSettings,
                preservedListPrice,
                result,
            );
            break;
        case 'supplierListPriceNew':
            syncTransportCostFromBreakdown(form);
            syncStampDutyFromBreakdown(form, pricingSettings);
            keepListPriceStickyAndSyncMarkup(
                form,
                pricingSettings,
                preservedListPrice,
                result,
            );
            break;
        case 'discounts':
            if (Array.isArray(rawValue)) {
                form.discounts = [...rawValue];
            }
            keepListPriceStickyAndSyncMarkup(
                form,
                pricingSettings,
                preservedListPrice,
                result,
            );
            break;
        case 'costUplifts':
            if (Array.isArray(rawValue)) {
                form.costUplifts = [...rawValue];
            }
            keepListPriceStickyAndSyncMarkup(
                form,
                pricingSettings,
                preservedListPrice,
                result,
            );
            break;
        case 'discount1':
        case 'discount2':
        case 'cashDiscount':
        case 'marketingDiscount':
        case 'squareMetersPerTruck':
            keepListPriceStickyAndSyncMarkup(
                form,
                pricingSettings,
                preservedListPrice,
                result,
            );
            break;
        case 'currency': {
            // Caller should leave form.currency as the previous currency
            // (use :model-value, not v-model) so we can convert list prices.
            // If the user already retyped pret lista in the target currency,
            // skip converting that field (convertSupplierListPriceNew: false).
            const previousCurrency = String(form.currency || 'ron');
            const nextCurrency = String(rawValue || 'ron');
            convertSupplierListPricesForCurrencyChange(
                form,
                previousCurrency,
                nextCurrency,
                pricingSettings,
                {
                    convertNew: options.convertSupplierListPriceNew !== false,
                    convertOld: options.convertSupplierListPriceOld !== false,
                },
            );
            form.currency = nextCurrency;
            syncTransportCostFromBreakdown(form);
            keepListPriceStickyAndSyncMarkup(
                form,
                pricingSettings,
                preservedListPrice,
                result,
            );
            break;
        }
        case 'netEntryPriceExVat':
            keepListPriceStickyAndSyncMarkup(
                form,
                pricingSettings,
                preservedListPrice,
                result,
            );
            applyNetEntryPriceEdit(form, rawValue, pricingSettings);
            syncMarkupActualFromListOverride(form, pricingSettings);
            break;
        case 'finalEntryPriceExVat':
            keepListPriceStickyAndSyncMarkup(
                form,
                pricingSettings,
                preservedListPrice,
                result,
            );
            applyFinalEntryPriceEdit(form, rawValue, pricingSettings);
            syncMarkupActualFromListOverride(form, pricingSettings);
            break;
        case 'entryPriceRonExVat':
            keepListPriceStickyAndSyncMarkup(
                form,
                pricingSettings,
                preservedListPrice,
                result,
            );
            applyEntryPriceRonExVatEdit(form, rawValue, pricingSettings);
            syncMarkupActualFromListOverride(form, pricingSettings);
            break;
        case 'entryPriceRonIncVat':
            keepListPriceStickyAndSyncMarkup(
                form,
                pricingSettings,
                preservedListPrice,
                result,
            );
            applyEntryPriceRonIncVatEdit(form, rawValue, pricingSettings);
            syncMarkupActualFromListOverride(form, pricingSettings);
            break;
        case 'listPriceRonExVat':
            applyListPriceRonExVatEdit(form, rawValue, pricingSettings);
            result.markupActualManual = true;
            break;
        case 'listPriceEuroExVat':
            applyListPriceEuroExVatEdit(form, rawValue, pricingSettings);
            result.markupActualManual = true;
            break;
        case 'listPriceRonIncVatFinal':
            applyListPriceRonIncVatFinalEdit(form, rawValue, pricingSettings);
            result.markupActualManual = true;
            break;
        default:
            break;
    }

    return result;
}

/**
 * Computed selling price (list RON inc. VAT) for a specific furnizor pricing row.
 *
 * @param {Record<string, unknown>} form
 * @param {Record<string, unknown>} pricing
 * @param {Record<string, unknown>} [pricingSettings]
 * @returns {number}
 */
export function computeFurnizorListPriceRonIncVatFinal(
    form,
    pricing,
    pricingSettings = DEFAULT_PRICING_SETTINGS,
) {
    const computed = calculateProductPricing(
        builderFormToCalculatorInput(mergeFormWithFurnizorPricing(form, pricing)),
        mergeListPricingSettings(pricingSettings),
    );
    return parseNumericInput(computed.listPriceRonIncVatFinal);
}

/**
 * True when draft save should offer copying the active selling price to other
 * furnizor tabs (multi-supplier product + selling price changed + others differ).
 *
 * @param {Record<string, unknown>} form
 * @param {number|string|null|undefined} activeListPrice
 * @param {number|string|null|undefined} sessionBaselineListPrice
 * @param {Record<string, unknown>} [pricingSettings]
 * @returns {boolean}
 */
export function shouldOfferPropagateSellingPriceToOtherFurnizors(
    form,
    activeListPrice,
    sessionBaselineListPrice,
    pricingSettings = DEFAULT_PRICING_SETTINGS,
) {
    const pricings = Array.isArray(form?.furnizorPricings) ? form.furnizorPricings : [];
    if (pricings.length < 2) {
        return false;
    }
    const activePrice = parseNumericInput(activeListPrice);
    if (!(activePrice > 0)) {
        return false;
    }
    const baselinePrice = parseNumericInput(sessionBaselineListPrice);
    if (Math.abs(activePrice - baselinePrice) < 0.005) {
        return false;
    }
    const activeKey = form.activeFurnizorPricingKey;
    return pricings.some((pricing) => {
        if (!pricing || pricing.clientKey === activeKey) {
            return false;
        }
        const otherPrice = computeFurnizorListPriceRonIncVatFinal(
            form,
            pricing,
            pricingSettings,
        );
        return Math.abs(otherPrice - activePrice) >= 0.005;
    });
}

/**
 * Copies the given selling price onto every non-active furnizor pricing row and
 * recalculates each row's adaos actual from its own entry price.
 *
 * @param {Record<string, unknown>} form
 * @param {number|string} listPriceRonIncVatFinal
 * @param {Record<string, unknown>} [pricingSettings]
 * @returns {string[]} clientKeys that were updated
 */
export function applySellingPriceToOtherFurnizorPricings(
    form,
    listPriceRonIncVatFinal,
    pricingSettings = DEFAULT_PRICING_SETTINGS,
) {
    persistActiveFurnizorPricingFromRoot(form);
    const pricings = Array.isArray(form.furnizorPricings) ? form.furnizorPricings : [];
    const activeKey = form.activeFurnizorPricingKey;
    const priceText = formatNumericInputValue(
        parseNumericInput(listPriceRonIncVatFinal),
        2,
    );
    if (!(parseNumericInput(priceText) > 0) || pricings.length < 2) {
        return [];
    }

    const updatedKeys = [];
    pricings.forEach((pricing) => {
        if (!pricing || pricing.clientKey === activeKey) {
            return;
        }
        const merged = mergeFormWithFurnizorPricing(form, pricing);
        applyPricingFieldEdit(
            merged,
            'listPriceRonIncVatFinal',
            priceText,
            pricingSettings,
        );
        pricing.overrideListPriceRonIncVatFinal =
            merged.overrideListPriceRonIncVatFinal;
        pricing.overrideListPriceRonIncVatFinalValue =
            merged.overrideListPriceRonIncVatFinalValue;
        pricing.useManualFinalPrice = merged.useManualFinalPrice;
        pricing.manualListPriceRonIncVatFinal =
            merged.manualListPriceRonIncVatFinal;
        pricing.markupActual = merged.markupActual;
        pricing.priceAdjustmentRatio = merged.priceAdjustmentRatio;
        updatedKeys.push(pricing.clientKey);
    });
    return updatedKeys;
}
